import {createReducer, on} from '@ngrx/store';
import {INITIAL_LOAD_STATUS_SUBSTATE, LoadStatusSubstate} from '@aviary/store/store.types';
import {FAILED, ListFilterData, LOADED, LOADING, RELOADING} from '@aviary/store/common/common.types';
import {CarouselDisplay, CatalogRecord, CatalogRecordAvailabilityDetail} from '@store/catalog/catalog.types';
import {CatalogActions} from '@store/catalog/catalog.actions';

export interface CatalogState {
  current: LoadStatusSubstate & {
    data?: CatalogRecord;
  };
  availability: LoadStatusSubstate & {
    data?: CatalogRecordAvailabilityDetail;
  };
  related: LoadStatusSubstate & {
    data?: CatalogRecord[];
  };
  carousels: LoadStatusSubstate & {
    data?: CarouselDisplay[]
  };
  list: LoadStatusSubstate & {
    data: CatalogRecord[];
    filterData: ListFilterData;
  };
  favorites: LoadStatusSubstate & {
    data: CatalogRecord[]
  };
  favoritesIds: LoadStatusSubstate & {
    data: number[]
  };
}

export const INITIAL_CATALOG_STATE: CatalogState = {
  current: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: undefined,
  },
  availability: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: undefined,
  },
  related: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: undefined,
  },
  carousels: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: []
  },
  list: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: [],
    filterData: {}
  },
  favorites: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: []
  },
  favoritesIds: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: []
  }
};

export const catalogState = createReducer(
  INITIAL_CATALOG_STATE,

  on(CatalogActions.loadCatalogRecord, (state, {id}) => {
    const reloading: boolean = state.current.data?.id === id;
    return {
      ...state,
      current: {
        ...state.current,
        data: reloading ? state.current.data : undefined,
        loadStatus: reloading ? RELOADING : LOADING
      }
    };
  }),
  on(CatalogActions.loadCatalogRecordSuccess, (state, {catalogRecord}) => ({
    ...state,
    current: {
      ...state.current,
      loadStatus: LOADED,
      data: catalogRecord
    }
  })),
  on(CatalogActions.loadCatalogRecordFailure, state => ({
    ...state,
    current: {
      ...state.current,
      loadStatus: FAILED
    }
  })),
  on(CatalogActions.loadCatalogAvailability, (state) => ({
    ...state,
    availability: {
      ...state.availability,
      loadStatus: LOADING
    }
  })),
  on(CatalogActions.loadCatalogAvailabilitySuccess, (state, {availability}) => ({
    ...state,
    availability: {
      ...state.availability,
      loadStatus: LOADED,
      data: availability
    }
  })),
  on(CatalogActions.loadCatalogAvailabilityFailure, state => ({
    ...state,
    availability: {
      ...state.availability,
      loadStatus: FAILED
    }
  })),
  on(CatalogActions.loadCatalogRelatedItems, (state) => ({
    ...state,
    related: {
      ...state.related,
      loadStatus: LOADING
    }
  })),
  on(CatalogActions.loadCatalogRelatedItemsSuccess, (state, {relatedItems}) => ({
    ...state,
    related: {
      ...state.related,
      loadStatus: LOADED,
      data: relatedItems
    }
  })),
  on(CatalogActions.loadCatalogRelatedItemsFailure, state => ({
    ...state,
    related: {
      ...state.related,
      loadStatus: FAILED
    }
  })),

  // CAROUSELS
  on(CatalogActions.loadCatalogCarouselsSuccess, (state, {listData}) => ({
    ...state,
    carousels: {
      loadStatus: LOADED,
      data: listData
    }
  })),
  on(CatalogActions.loadCatalogCarouselsFailure, state => ({
    ...state,
    carousels: {
      ...INITIAL_CATALOG_STATE.carousels,
      loadStatus: FAILED
    }
  })),

  // SEARCH
  on(CatalogActions.searchCatalog, state => ({
      ...state,
      list: {
        ...state.list,
        loadStatus: LOADING
      }
    })
  ),
  on(CatalogActions.searchCatalogSuccess, (state, {listData: {data, filterData}}) => ({
      ...state,
      list: {
        ...state.list,
        loadStatus: LOADED,
        data,
        filterData
      }
    })
  ),
  on(CatalogActions.searchCatalogFailure, state => ({
      ...state,
      list: {
        ...INITIAL_CATALOG_STATE.list,
        loadStatus: FAILED
      }
    })
  ),

  // FAVORITES
  on(CatalogActions.loadFavorites, state => ({
      ...state,
      favorites: {
        ...state.favorites,
        loadStatus: LOADING
      }
    })
  ),
  on(CatalogActions.loadFavoritesSuccess, (state, {listData: {data}}) => ({
    ...state,
    favorites: {
      loadStatus: LOADED,
      data
    }
  })),
  on(CatalogActions.loadFavoritesFailure, state => ({
    ...state,
    favorites: {
      ...INITIAL_CATALOG_STATE.favorites,
      loadStatus: FAILED
    }
  })),
  on(CatalogActions.loadFavoritesIds, state => ({
      ...state,
      favoritesIds: {
        ...state.favoritesIds,
        loadStatus: LOADING
      }
    })
  ),
  on(CatalogActions.loadFavoritesIdsSuccess, (state, {ids}) => ({
    ...state,
    favoritesIds: {
      loadStatus: LOADED,
      data: ids
    }
  })),
  on(CatalogActions.loadFavoritesIdsFailure, state => ({
    ...state,
    favoritesIds: {
      ...INITIAL_CATALOG_STATE.favoritesIds,
      loadStatus: FAILED
    }
  })),
);
